import { useState } from "react";
import { format, isValid, parseISO } from "date-fns";
import { ChevronDown, ChevronRight, ExternalLink, MapPin, User } from "lucide-react";
import { Link } from "react-router";
import { useMember } from "@/contexts/memberContext";
import { isElevatedUser } from "@/utils/userPermissions";
import type { RecurringConflictDate } from "@/types/CanBookValidation";

type RecurringConflictListProps = {
  conflicts: RecurringConflictDate[];
  /** The proposed booking start time (HHmm) — every occurrence is at this time. */
  proposedTime: string;
};

/** "1000" → "10:00am", "1330" → "1:30pm". */
const formatTime12h = (hhmm: string): string => {
  const h = parseInt(hhmm.slice(0, 2), 10);
  const m = hhmm.slice(2, 4);
  const period = h < 12 ? "am" : "pm";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${m}${period}`;
};

const formatConflictDate = (isoDate: string): string => {
  // Guard with isValid rather than catching a thrown RangeError: parseISO returns an Invalid Date
  // for non-ISO strings and format() would otherwise throw "Invalid time value". Fall back to raw.
  const parsed = parseISO(isoDate);
  return isValid(parsed) ? format(parsed, "do MMMM") : isoDate;
};

/**
 * Lists the dates on which a recurring booking would clash, each expandable to a brief card
 * showing what's blocking it (owner, date, room) with a link to that booking.
 */
/** Show at most this many conflicting dates; the rest are summarised as "and X more". */
const MAX_VISIBLE = 3;

export const RecurringConflictList = ({ conflicts, proposedTime }: RecurringConflictListProps) => {
  const [openKeys, setOpenKeys] = useState<Set<string>>(new Set());
  const { member } = useMember();
  // Only admins / practice managers may see whose booking blocks a date, or open it. Members get
  // the dates, times and room — enough to reschedule — and nothing identifying.
  const canOpenConflicts = isElevatedUser(member);

  if (conflicts.length === 0) return null;

  // Earliest first, then cap — the list can run to dozens of occurrences.
  const ordered = [...conflicts].sort((a, b) => a.date.localeCompare(b.date));
  const visible = ordered.slice(0, MAX_VISIBLE);
  const hiddenCount = ordered.length - visible.length;

  const toggle = (key: string) =>
    setOpenKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });

  return (
    <div
      className="mt-2 rounded-md border border-amber-200 bg-amber-50 p-2"
      data-testid="recurring-conflict-list"
    >
      <p className="text-xs font-semibold text-amber-900">
        Recurring booking conflicts found on {conflicts.length} date{conflicts.length === 1 ? "" : "s"}:
      </p>
      <ul className="mt-1 flex flex-col divide-y divide-amber-100">
        {visible.map((conflict, idx) => {
          const key = `${conflict.date}-${idx}`;
          const isOpen = openKeys.has(key);
          const cb = conflict.conflictingBooking;
          return (
            <li key={key} className="py-1">
              <button
                type="button"
                onClick={() => toggle(key)}
                aria-expanded={isOpen}
                className="flex w-full items-center gap-1 text-left text-xs font-medium text-amber-900 hover:text-amber-950"
                data-testid={`recurring-conflict-row-${conflict.date}`}
              >
                {isOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                <span data-testid={`recurring-conflict-label-${conflict.date}`}>
                  {formatConflictDate(conflict.date)} at{" "}
                  {/* The BLOCKING booking's time when we know it — each clash can sit at a different
                      time, which is what makes the list actionable. Falls back to the proposed time
                      for conflicts with no booking reference (e.g. amenity clashes). */}
                  {formatTime12h(cb?.startTime ?? proposedTime)}
                </span>
              </button>

              {isOpen && (
                <div className="ml-5 mt-1 rounded border border-amber-200 bg-white p-2 text-xs text-gray-700">
                  {cb ? (
                    <>
                      {/* Owner is shown only to elevated users. A member cannot see other members'
                          bookings anywhere else in the product, so naming who blocks them here would
                          leak it. The server redacts this for members too — this is the second layer,
                          not the only one. */}
                      {canOpenConflicts && (
                        <div className="flex items-center gap-1.5">
                          <User className="h-3.5 w-3.5 text-gray-500" />
                          <span className="font-medium">{cb.ownerName ?? "Another member"}</span>
                        </div>
                      )}
                      <div className={`flex items-center gap-1.5 ${canOpenConflicts ? "mt-1" : ""}`}>
                        <MapPin className="h-3.5 w-3.5 text-gray-500" />
                        <span>
                          {cb.roomName ?? "this room"} · {formatTime12h(cb.startTime)}–{formatTime12h(cb.endTime)}
                        </span>
                      </div>
                      {canOpenConflicts && cb.id && (
                        <Link
                          to={`/view-booking/${cb.id}`}
                          className="mt-1.5 inline-flex items-center gap-1 text-indigo-600 hover:text-indigo-800 hover:underline"
                          data-testid={`recurring-conflict-link-${conflict.date}`}
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                          View booking{cb.shortCode ? ` ${cb.shortCode.toUpperCase()}` : ""}
                        </Link>
                      )}
                    </>
                  ) : (
                    <span>{conflict.details}</span>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>
      {hiddenCount > 0 && (
        <p className="mt-1 pl-5 text-xs text-amber-800" data-testid="recurring-conflict-more">
          …and {hiddenCount} more conflict{hiddenCount === 1 ? "" : "s"}
        </p>
      )}
    </div>
  );
};

export default RecurringConflictList;
